import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;
const slug = (s) => s.replace(/\s+/g, '-').toLowerCase();

export const SteelPricingPanel = ({ prices, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cost, setCost] = useState('');
  const [sell, setSell] = useState('');
  const unset = prices.filter((p) => !(p.price_per_lb > 0) || !(p.sell_per_lb > 0)).length;

  const save = async (materialType) => {
    try {
      await axios.put(`${API}/steel-pricing/${materialType}`, { price_per_lb: parseFloat(cost) || 0, sell_per_lb: parseFloat(sell) || 0 });
      toast.success(`${materialType}: cost $${(parseFloat(cost) || 0).toFixed(2)}/lb · sell $${(parseFloat(sell) || 0).toFixed(2)}/lb`);
      setEditing(null); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="steel-pricing-panel">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-4 py-3 flex items-center justify-between text-left" data-testid="steel-pricing-toggle">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><DollarSign className="w-4 h-4 text-emerald-600" /> Stock steel pricing ($/lb)
            {unset > 0 && <span className="text-[10px] font-semibold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{unset} not set</span>}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Cost (what we pay) and Sell (what the job is charged) per material type — feeds the Materials by WO report and running cost above.</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && (
        <div className="divide-y divide-slate-100 border-t border-slate-200">
          {prices.map((p) => (
            <div key={p.material_type} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`steel-price-${slug(p.material_type)}`}>
              <div><span className="text-sm text-slate-900 font-medium">{p.material_type}</span>{p.updated_by && <span className="text-[10px] text-slate-500 ml-2">set by {p.updated_by}</span>}</div>
              {editing === p.material_type ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-600">Cost $/lb</span>
                  <input type="number" step="0.01" value={cost} onFocus={(e) => e.target.select()} onChange={(e) => setCost(e.target.value)} autoFocus
                    data-testid="steel-cost-input" className="w-20 px-2 py-1.5 border border-blue-500 rounded text-sm text-center outline-none" />
                  <span className="text-xs text-slate-600">Sell $/lb</span>
                  <input type="number" step="0.01" value={sell} onFocus={(e) => e.target.select()} onChange={(e) => setSell(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(p.material_type); else if (e.key === 'Escape') setEditing(null); }}
                    data-testid="steel-sell-input" className="w-20 px-2 py-1.5 border border-emerald-500 rounded text-sm text-center outline-none" />
                  <button onClick={() => save(p.material_type)} data-testid="steel-price-save" className="px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium">Save</button>
                  <button onClick={() => setEditing(null)} className="text-xs text-slate-500 px-2">Cancel</button>
                </div>
              ) : (
                <button onClick={() => { setEditing(p.material_type); setCost(p.price_per_lb || ''); setSell(p.sell_per_lb || ''); }}
                  data-testid={`steel-price-edit-${slug(p.material_type)}`} className="text-sm font-mono px-3 py-1.5 rounded border border-slate-200 hover:border-slate-400 text-slate-800">
                  {p.price_per_lb > 0 ? `cost $${p.price_per_lb.toFixed(2)}` : <span className="text-rose-600">set cost</span>} · {p.sell_per_lb > 0 ? `sell $${Number(p.sell_per_lb).toFixed(2)}` : <span className="text-amber-600">sell not set</span>}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
